import { Minus, Plus, Trash2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { pitchClass } from '@/lib/pitch';
import { PRESETS, QUALITIES, chordName, presetSteps, shiftProg } from '@/lib/droneChords';
import { PlayRow } from '@/components/drone/DronePlayRow';
import { DroneSongs } from '@/components/drone/DroneSongs';

const MAX_STEPS = 16;
const MAX_BARS = 8;
const iconBtn = 'h-9 w-8 sm:w-9 shrink-0 rounded-lg border border-white/10 flex items-center justify-center hover:bg-white/5 disabled:opacity-30 active:scale-95 transition-transform';
const selectCls = 'h-9 rounded-lg bg-[#0A0A0A] border border-white/10 px-2 text-sm text-white focus:outline-none focus:border-sky-400';

const StepRow = ({ i, step, flats, current, onChange, onRemove, canRemove }) => (
  <li data-testid={`drone-prog-step-${i}`} className={`flex items-center gap-1.5 rounded-xl p-1.5 border transition-colors ${current ? 'border-sky-400 bg-sky-400/10' : 'border-white/5'}`}>
    <span data-testid={`drone-prog-step-name-${i}`} className={`hidden sm:block w-16 shrink-0 text-center font-mono font-bold text-base leading-none ${current ? 'text-sky-300' : 'text-white/80'}`}>{chordName(step.pc, step.quality, flats)}</span>
    <select data-testid={`drone-prog-step-note-${i}`} aria-label="Nota" value={step.pc} onChange={(e) => onChange({ pc: Number(e.target.value) })} className={`${selectCls} w-14`}>
      {Array.from({ length: 12 }, (_, pc) => <option key={pc} value={pc}>{pitchClass(pc, flats)}</option>)}
    </select>
    <select data-testid={`drone-prog-step-quality-${i}`} aria-label="Tipo accordo" value={step.quality} onChange={(e) => onChange({ quality: e.target.value })} className={`${selectCls} w-[4.5rem] shrink-0`}>
      {Object.entries(QUALITIES).map(([q, d]) => <option key={q} value={q}>{d.short}</option>)}
    </select>
    <button data-testid={`drone-prog-step-bars-down-${i}`} aria-label="Meno battute" disabled={step.bars <= 1} onClick={() => onChange({ bars: step.bars - 1 })} className={iconBtn}><Minus className="w-3.5 h-3.5" /></button>
    <span data-testid={`drone-prog-step-bars-${i}`} className="w-6 text-center font-mono text-xs">{step.bars}</span>
    <button data-testid={`drone-prog-step-bars-up-${i}`} aria-label="Più battute" disabled={step.bars >= MAX_BARS} onClick={() => onChange({ bars: step.bars + 1 })} className={iconBtn}><Plus className="w-3.5 h-3.5" /></button>
    <button data-testid={`drone-prog-step-remove-${i}`} aria-label="Elimina accordo" disabled={!canRemove} onClick={onRemove} className={iconBtn}><Trash2 className="w-3.5 h-3.5" /></button>
  </li>
);

export const DroneProgression = ({ drone, flats }) => {
  const { cfg, update, prog, progOn, updateProg, stepIdx, barCount } = drone;
  const offset = cfg.offsetMs || 0;
  const steps = prog.steps;
  const setSteps = (next) => updateProg({ steps: next });
  const applyPreset = (id) => {
    const p = PRESETS.find((x) => x.id === id);
    if (p) updateProg({ steps: presetSteps(p, cfg.pc), keyPc: cfg.pc, enabled: true });
  };
  const following = progOn && barCount > 0;
  const totalBars = steps.reduce((s, st) => s + st.bars, 0);

  return (
    <div data-testid="drone-progression" className={`rounded-xl border p-3 flex flex-col gap-3 ${progOn ? 'border-sky-400/30' : 'border-white/5'}`}>
      <label className="flex items-center justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">Bordone a tempo · accordi</span>
          <span className="text-[11px] text-white/40 mt-1">Cambia sul primo quarto di ogni battuta mentre suonano Player, Sequencer o il click del Tuner</span>
        </span>
        <Switch data-testid="drone-prog-toggle" checked={progOn} onCheckedChange={(v) => updateProg({ enabled: v, ...(v ? shiftProg(prog, cfg.pc) : {}) })} />
      </label>

      {progOn && (
        <>
          <PlayRow drone={drone} />
          <div className="flex items-center gap-2">
            <select data-testid="drone-prog-preset" aria-label="Progressione pronta" value="" onChange={(e) => applyPreset(e.target.value)} className={`${selectCls} flex-1 min-w-0`}>
              <option value="" disabled>{`Preset in ${pitchClass(cfg.pc, flats)}…`}</option>
              {PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <span data-testid="drone-prog-status" className={`shrink-0 text-[10px] uppercase tracking-[0.2em] ${following ? 'text-sky-300' : 'text-white/40'}`}>
              {following ? `Battuta ${((barCount - 1) % totalBars) + 1}/${totalBars}` : 'In attesa del tempo'}
            </span>
          </div>
          <ol className="flex flex-col gap-1.5">
            {steps.map((s, i) => (
              <StepRow
                key={i}
                i={i}
                step={s}
                flats={flats}
                current={i === stepIdx}
                canRemove={steps.length > 1}
                onChange={(p) => setSteps(steps.map((x, j) => (j === i ? { ...x, ...p } : x)))}
                onRemove={() => setSteps(steps.filter((_, j) => j !== i))}
              />
            ))}
          </ol>
          <button
            data-testid="drone-prog-add"
            disabled={steps.length >= MAX_STEPS}
            onClick={() => setSteps([...steps, { ...steps[steps.length - 1] }])}
            className="h-10 rounded-xl border border-dashed border-white/15 text-[11px] uppercase tracking-[0.2em] text-white/70 flex items-center justify-center gap-2 hover:bg-white/5 disabled:opacity-30"
          >
            <Plus className="w-4 h-4" /> Aggiungi accordo
          </button>
          <div className="flex flex-col gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Regolazione fine cambio accordo</span>
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-white/40">Anticipo</span>
              <Slider data-testid="drone-prog-offset" value={[offset]} min={-100} max={200} step={5} onValueChange={(v) => update({ offsetMs: v[0] })} className="flex-1" />
              <span className="text-[10px] text-white/40">Ritardo</span>
              <button data-testid="drone-prog-offset-value" onClick={() => update({ offsetMs: 0 })} title="Azzera" className="w-16 text-right font-mono text-xs text-sky-300">{offset > 0 ? '+' : ''}{offset} ms</button>
            </div>
            <span className="text-[11px] text-white/35">Il cambio è agganciato al click: sposta il cursore solo se senti il bordone in ritardo o in anticipo (es. cuffie Bluetooth). Tocca il valore per azzerare.</span>
          </div>
          <DroneSongs drone={drone} flats={flats} />
        </>
      )}
    </div>
  );
};
